import { PERMISSIONS } from 'api/User/types'
import classNames from 'classnames'
import CommonIcon from 'CommonIcon'
import { InfoElement } from 'components'
import { InfoArrow } from 'components/InfoArrow'
import { CurrencyValue } from 'CurrencyValue'
import type { DetailsData } from 'data/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import RestrictedView from 'RestrictedView'
import { thousandsSeparator } from 'utils'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import useStyles from './useStyles'
import { getCurrencyValue, getPercent } from './utils'

interface Props {
  data: DetailsData
}

function DetailsL4L({ data }: Props) {
  const { t: translate } = useTranslation()
  const classes = useStyles()

  return (
    <Grid container item xs={9}>
      <Grid container item xs={12} className={classes.row1} mt="0.7rem">
        <Grid container item xs={5} alignItems="baseline" display="row">
          <CurrencyValue baseFontSize={1.75} currency={data.currency} value={data.revenueCurrentL4L} />
          <InfoElement className={classes.percent} percent={data.revenuePercentL4L} />
        </Grid>
        <Grid container item xs={7} display="row">
          <Box className={`fm fm-returns ${classes.label}`} alignSelf="baseline" />
          {getCurrencyValue(data.returnsValueCurrentL4L, data.currency, translate)} (
          {getPercent(data.returnsValueShareCurrentL4L, translate)}){' | '}
          {getCurrencyValue(data.revenueWOReturnsCurrentL4L, data.currency, translate)}
        </Grid>
      </Grid>

      <Grid container item xs={5} alignItems="baseline" display="row" mt="0.25rem">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <i className={`fm fm-margin ${classes.label}`} />
          <div className={classes.marginPercent}>{getPercent(data.marginCurrentL4L, translate)}</div>
          <div className={classes.marginValue}>{getCurrencyValue(data.marginValueCurrentL4L, data.currency, translate)}</div>
          <InfoArrow current={data.marginValueCurrentL4L} compared={data.marginValueComparedL4L} />
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row" mt="0.25rem">
        <Box fontWeight="bold" ml="1rem">
          {getCurrencyValue(data.revenuePerStoreCurrentL4L, data.currency, translate)} /
        </Box>
        <i className={classNames('fm fm-shop', classes.label)} />
        <InfoElement className={classes.percent} percent={data.revenuePerStorePercentL4L} />
      </Grid>

      <Grid container item xs={5} display="row">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <CommonIcon className={classNames(classes.label, classes.lastYear)} name="lastYear" />
          <div className={classes.marginPercent}>{getPercent(data.marginComparedL4L, translate)}</div>
          <div className={classes.marginComparedValue}>
            {getCurrencyValue(data.marginValueComparedL4L, data.currency, translate)}
          </div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row">
        <i className={classNames('fm fm-traffic', classes.label)} />
        {thousandsSeparator(data.trafficPerStoreCurrentL4L)}
        <InfoElement className={classes.percent} percent={data.trafficPerStorePercentL4L} />
        <div className={classNames(classes.label, classes.avg)}>m²</div>
        {getCurrencyValue(data.floorSpaceCurrentL4L, 'm²', translate, 0)}
        <InfoElement className={classes.percent} percent={data.floorSpacePercentL4L} />
      </Grid>

      <Grid container item xs={12} className={classes.row3} alignItems="baseline">
        <Grid container item xs={5}>
          <i className={classNames('fm fm-oms', classes.label)} />
          <div className={classes.marginPercent}>{getPercent(data.ordersValueOMSShareCurrentL4L, translate)}</div>
          <Box className={classes.marginComparedValue}>
            {getCurrencyValue(data.ordersValueOMSCurrentL4L, data.currency, translate)}
          </Box>
        </Grid>
        <Grid container item xs={6} alignItems="baseline">
          <i className={`fm fm-instore ${classes.label}`} />
          {getPercent(data.ordersValueInStoreShareCurrentL4L, translate)}
          {' | '}
          {getCurrencyValue(data.ordersValueInStoreCurrentL4L, data.currency, translate)}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default DetailsL4L
